import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Modal } from 'react-bootstrap';
import type { User } from '../../../types';
import type { ChangeCatalogItem, ChangeRequest, ChangeWorkGroup } from '../../../types/changes';
import { changesApi } from '../../../services/changesApi';
import { getApiErrorMessage } from '../../../utils/apiError';
import {
  canAddReviewArea,
  createLikePackageGroupIds,
  flattenInternalSlots,
  internalReviewKey,
  internalSlotsForReviewKeys,
  leftoverGroups,
  leftoverManualInternalSlots,
  manualPackageExternalOptions,
  manualPackageInternalOptions,
  nextDuplicateReviewKey,
  occupyingExternalGroupIds,
  occupyingInternalReviewKeys,
  parseExternalLineKey,
  parseInternalReviewKey,
  resolveInternalSlotByReviewKey,
  slotReviewKey,
} from '../changeCreateReview';
import {
  computeCreateSubtaskReviewPermissions,
  subtaskRequesterPolicyFromFormSchema,
  type SubtaskRequesterPolicy,
} from '../changeSubtaskCreateReviewPermissions';
import { isChangeManager } from '../changePermissions';
import { useAuth } from '../../../context/AuthContext';
import {
  buildSubtaskPackagePayload,
  emptyExternalDraft,
  emptyInternalDraft,
  enrichInternalDraftFromTemplate,
  enrichUnlockedInternalDrafts,
  resolvePackageSelectionsForSave,
  mergeExistingWithSuggestedPackage,
  seedPackageForManualRedispatch,
  persistInternalTemplateDraftsBeforePackage,
  uploadSubtaskPackageFiles,
  validateSubtaskPackage,
} from '../changeSubtaskPackageHelpers';
import { shouldPersistInternalTemplatesBeforePackage } from '../changeSubtaskPackagePersistPolicy';
import { augmentWorkGroupsWithCatalogTemplates } from '../changeAugmentWorkGroupsWithTemplates';
import ChangeCreateSubtaskReview, {
  type ExternalReviewDraft,
  type InternalReviewDraft,
} from './ChangeCreateSubtaskReview';

interface Props {
  show: boolean;
  change: ChangeRequest;
  users: User[];
  allowInternalSubtasks: boolean;
  allowExternalSubtasks: boolean;
  prefillLikeCreate?: boolean;
  /** Aba Subtarefas: sem limite por área e ignora política do tipo. */
  unrestrictedManualCreate?: boolean;
  onHide: () => void;
  onSaved: () => Promise<void> | void;
}

const ChangeAddSubtasksPackageModal: React.FC<Props> = ({
  show,
  change,
  users,
  allowInternalSubtasks,
  allowExternalSubtasks,
  prefillLikeCreate = false,
  unrestrictedManualCreate = false,
  onHide,
  onSaved,
}) => {
  const [workGroups, setWorkGroups] = useState<ChangeWorkGroup[]>([]);
  const [incidentTargets, setIncidentTargets] = useState<{
    groups: Array<{ id: number; name: string }>;
    workflows: Array<{ id: number; name: string; group_ids: number[] }>;
  }>({ groups: [], workflows: [] });
  const [taskTypes, setTaskTypes] = useState<ChangeCatalogItem[]>([]);
  const [internalDrafts, setInternalDrafts] = useState<Record<string, InternalReviewDraft>>({});
  const [externalDrafts, setExternalDrafts] = useState<Record<string, ExternalReviewDraft>>({});
  const [reviewInternalKeys, setReviewInternalKeys] = useState<string[]>([]);
  const [reviewExternalKeys, setReviewExternalKeys] = useState<string[]>([]);
  const [reviewEditing, setReviewEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [requesterPolicy, setRequesterPolicy] = useState<SubtaskRequesterPolicy>({
    allow_create: false,
    allow_edit: false,
    allow_delete: false,
  });
  const { user } = useAuth();
  const manager = isChangeManager(user);
  const reviewPermissions = useMemo(
    () =>
      computeCreateSubtaskReviewPermissions({
        isChangeManager: manager,
        currentUserId: user?.id,
        requesterId: change.requester_id || 0,
        changeOwnerId: change.change_owner_id,
        createdBy: change.created_by ?? undefined,
        policy: requesterPolicy,
        existingChangePackage: true,
      }),
    [manager, user?.id, change.requester_id, change.change_owner_id, change.created_by, requesterPolicy],
  );
  const templatePersistMode = useMemo(() => {
    if (!allowInternalSubtasks) return null;
    if (manager) return 'global' as const;
    if (reviewPermissions.canAdd) return 'personal' as const;
    return null;
  }, [allowInternalSubtasks, manager, reviewPermissions.canAdd]);

  const catalogTemplatePersistMode = useMemo(
    () =>
      shouldPersistInternalTemplatesBeforePackage({
        templatePersistMode,
        prefillLikeCreate,
      })
        ? templatePersistMode
        : null,
    [templatePersistMode, prefillLikeCreate],
  );

  const existingInternalKeys = useMemo(
    () => occupyingInternalReviewKeys(change.subtasks),
    [change.subtasks],
  );
  const existingExternalIds = useMemo(
    () => occupyingExternalGroupIds(change.subtasks),
    [change.subtasks],
  );

  useEffect(() => {
    if (!show) return undefined;
    let cancelled = false;
    setError(null);
    setReviewInternalKeys([]);
    setReviewExternalKeys([]);
    setInternalDrafts({});
    setExternalDrafts({});
    setReviewEditing(false);
    setReady(false);
    void Promise.all([
      changesApi.listWorkGroups({ only_active: true }),
      changesApi
        .listInternalSubtaskTemplates({ only_active: true, catalog_only: manager })
        .catch(() => ({ items: [] })),
      changesApi.listIncidentTargets().catch(() => ({ groups: [], workflows: [] })),
      changesApi.listCatalog('task-types', { only_active: true }).catch(() => ({ items: [] })),
      changesApi.getSubtaskFormSchema().catch(() => null),
    ])
      .then(([areas, templateCatalog, targets, catalog, subtaskSchema]) => {
        if (cancelled) return;
        const items = augmentWorkGroupsWithCatalogTemplates(
          areas.items || [],
          templateCatalog.items || [],
        );
        const workflows = targets.workflows || [];
        setWorkGroups(items);
        setIncidentTargets({
          groups: targets.groups || [],
          workflows,
        });
        setTaskTypes(catalog.items || []);
        setRequesterPolicy(subtaskRequesterPolicyFromFormSchema(subtaskSchema));
        if (prefillLikeCreate && !unrestrictedManualCreate) {
          const seeded = createLikePackageGroupIds(items, {
            changeType: change.change_type,
            allowInternal: allowInternalSubtasks,
            allowExternal: allowExternalSubtasks,
            workflows,
            occupiedGroupIds: existingExternalIds,
            occupiedInternalKeys: existingInternalKeys,
            occupiedExternalIds: existingExternalIds,
            policy: change.change_type_policy,
          });
          setReviewInternalKeys(seeded.internalKeys);
          setReviewExternalKeys(seeded.externalIds.map((id) => String(id)));
          setInternalDrafts(
            Object.fromEntries(
              flattenInternalSlots(items)
                .filter((slot) => seeded.internalKeys.includes(slotReviewKey(slot)))
                .map((slot) => [slotReviewKey(slot), emptyInternalDraft(slot)]),
            ),
          );
          setExternalDrafts(
            Object.fromEntries(
              items
                .filter((group) => seeded.externalIds.includes(group.group_id))
                .map((group) => [group.group_id, emptyExternalDraft(group, workflows)]),
            ),
          );
        } else if (unrestrictedManualCreate && prefillLikeCreate) {
          const seeded = seedPackageForManualRedispatch(change.subtasks, items, workflows);
          setReviewInternalKeys(seeded.internalKeys);
          setReviewExternalKeys(seeded.externalKeys);
          setInternalDrafts(seeded.internalDrafts);
          setExternalDrafts(seeded.externalDrafts);
          void enrichUnlockedInternalDrafts(items, seeded.internalDrafts).then((enriched) => {
            if (cancelled) return;
            setInternalDrafts(enriched);
          });
        } else if (unrestrictedManualCreate) {
          setReviewInternalKeys([]);
          setReviewExternalKeys([]);
          setExternalDrafts({});
          setInternalDrafts({});
        } else {
          const merged = mergeExistingWithSuggestedPackage(change.subtasks, items, workflows, {
            changeType: change.change_type,
            allowInternal: allowInternalSubtasks,
            allowExternal: allowExternalSubtasks,
            policy: change.change_type_policy,
          });
          setReviewInternalKeys(merged.internalKeys);
          setReviewExternalKeys(merged.externalIds.map((id) => String(id)));
          setExternalDrafts(
            Object.fromEntries(
              Object.entries(merged.externalDrafts).map(([key, draft]) => [String(key), draft]),
            ),
          );
          setInternalDrafts(merged.internalDrafts);
          void enrichUnlockedInternalDrafts(items, merged.internalDrafts).then((enriched) => {
            if (cancelled) return;
            setInternalDrafts(enriched);
          });
        }
      })
      .finally(() => {
        if (!cancelled) setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [
    show,
    change.id,
    change.change_type,
    change.change_type_policy,
    change.subtasks,
    allowInternalSubtasks,
    allowExternalSubtasks,
    prefillLikeCreate,
    unrestrictedManualCreate,
    manager,
  ]);

  const internalGroups = useMemo(
    () =>
      internalSlotsForReviewKeys(workGroups, reviewInternalKeys, {
        allowDuplicateLines: unrestrictedManualCreate,
      }),
    [workGroups, reviewInternalKeys, unrestrictedManualCreate],
  );
  const externalGroups = useMemo((): ChangeWorkGroup[] => {
    const rows: ChangeWorkGroup[] = [];
    reviewExternalKeys.forEach((key) => {
      const { groupId } = parseExternalLineKey(key);
      const group = workGroups.find((item) => item.group_id === groupId);
      if (!group) return;
      rows.push({ ...group, review_key: key });
    });
    return rows;
  }, [workGroups, reviewExternalKeys]);

  const attachTemplateSlotToPackage = (templateId: number, groupId: number, items: ChangeWorkGroup[]) => {
    const templateKey = internalReviewKey(groupId, templateId);
    const slot =
      flattenInternalSlots(items).find(
        (item) => item.group_id === groupId && item.internal_subtask_template_id === templateId,
      ) || resolveInternalSlotByReviewKey(items, templateKey);
    if (!slot) return;
    const baseKey = slotReviewKey(slot);
    const manualKey = internalReviewKey(groupId, 0);
    const key = baseKey;
    if (unrestrictedManualCreate) {
      setReviewInternalKeys((prev) => {
        const withoutManual = prev.filter((existing) => {
          const parsed = parseInternalReviewKey(existing);
          return !(parsed.groupId === groupId && parsed.templateId === 0);
        });
        const alreadyHasTemplate = withoutManual.some((existing) => {
          const parsed = parseInternalReviewKey(existing);
          return parsed.groupId === groupId && parsed.templateId === templateId;
        });
        if (alreadyHasTemplate) return withoutManual;
        const lineKey = withoutManual.includes(key)
          ? nextDuplicateReviewKey(withoutManual, key)
          : key;
        return [...withoutManual, lineKey];
      });
      setInternalDrafts((prev) => {
        const manualKeys = Object.keys(prev).filter((existing) => {
          const parsed = parseInternalReviewKey(existing);
          return parsed.groupId === groupId && parsed.templateId === 0;
        });
        const carried =
          manualKeys.map((existing) => prev[existing]).find(Boolean) ||
          prev[key] ||
          emptyInternalDraft({ ...slot, review_key: key });
        const next = { ...prev };
        manualKeys.forEach((existing) => {
          delete next[existing];
        });
        const templateKeys = Object.keys(next).filter((existing) => {
          const parsed = parseInternalReviewKey(existing);
          return parsed.groupId === groupId && parsed.templateId === templateId;
        });
        if (!templateKeys.length) {
          next[key] = { ...(next[key] || carried) };
        }
        return next;
      });
      return;
    }
    setReviewInternalKeys((prev) => {
      const withoutManual = prev.filter((existing) => {
        if (existing === manualKey) return false;
        const parsed = existing.split(':');
        return Number(parsed[0]) !== groupId || Number(parsed[1]) === templateId;
      });
      return withoutManual.includes(key) ? withoutManual : [...withoutManual, key];
    });
    setInternalDrafts((prev) => {
      const carried = prev[manualKey] || prev[key] || emptyInternalDraft(slot);
      const next = { ...prev };
      delete next[manualKey];
      return { ...next, [key]: carried };
    });
  };

  const handleTemplateSaved = (templateId: number, groupId: number) => {
    void Promise.all([
      changesApi.listWorkGroups({ only_active: true }),
      changesApi
        .listInternalSubtaskTemplates({ only_active: true, catalog_only: manager })
        .catch(() => ({ items: [] })),
    ]).then(([areas, templateCatalog]) => {
      const items = augmentWorkGroupsWithCatalogTemplates(
        areas.items || [],
        templateCatalog.items || [],
      );
      setWorkGroups(items);
      attachTemplateSlotToPackage(templateId, groupId, items);
    });
  };

  const handleTemplateDeleted = (templateId: number) => {
    void changesApi.listWorkGroups({ only_active: true }).then((areas) => {
      const items = areas.items || [];
      setWorkGroups(items);
      setReviewInternalKeys((prev) =>
        prev.filter((key) => {
          const slot = flattenInternalSlots(items).find((item) => slotReviewKey(item) === key);
          return slot?.internal_subtask_template_id !== templateId;
        }),
      );
    });
  };

  const leftoverInternals = useMemo(() => {
    if (unrestrictedManualCreate) {
      return manualPackageInternalOptions(workGroups);
    }
    return leftoverManualInternalSlots(
      workGroups,
      [...reviewInternalKeys, ...Array.from(existingInternalKeys)],
      {
        allowInternal: true,
        changeType: change.change_type,
        policy: change.change_type_policy,
        ignoreTypeInternalPolicy: true,
      },
    );
  }, [
    workGroups,
    reviewInternalKeys,
    existingInternalKeys,
    change.change_type,
    change.change_type_policy,
    unrestrictedManualCreate,
  ]);

  const leftoverExternals = useMemo(() => {
    if (unrestrictedManualCreate) {
      return manualPackageExternalOptions(workGroups);
    }
    return leftoverGroups(
      workGroups,
      reviewExternalKeys.map((key) => parseExternalLineKey(key).groupId),
      (group) => allowExternalSubtasks && canAddReviewArea(group),
    );
  }, [
    workGroups,
    reviewExternalKeys,
    allowExternalSubtasks,
    unrestrictedManualCreate,
  ]);

  const selectedInternals = useMemo(
    () =>
      internalGroups.filter((group) => internalDrafts[slotReviewKey(group)]?.selected !== false),
    [internalGroups, internalDrafts],
  );
  const selectedExternals = useMemo(
    () =>
      externalGroups.filter((group) => {
        const key = group.review_key || String(group.group_id);
        return externalDrafts[key]?.selected !== false;
      }),
    [externalGroups, externalDrafts],
  );

  const patchInternal = (reviewKey: string, patch: Partial<InternalReviewDraft>) => {
    const slot = resolveInternalSlotByReviewKey(workGroups, reviewKey);
    if (!slot) return;
    setInternalDrafts((prev) => ({
      ...prev,
      [reviewKey]: { ...(prev[reviewKey] || emptyInternalDraft(slot)), ...patch },
    }));
  };

  const patchExternal = (lineKey: string, patch: Partial<ExternalReviewDraft>) => {
    const { groupId } = parseExternalLineKey(lineKey);
    const group = workGroups.find((item) => item.group_id === groupId);
    if (!group) return;
    setExternalDrafts((prev) => ({
      ...prev,
      [lineKey]: {
        ...(prev[lineKey] || emptyExternalDraft(group, incidentTargets.workflows)),
        ...patch,
      },
    }));
  };

  const handleSave = async () => {
    const { newInternals, newExternals } = resolvePackageSelectionsForSave({
      unrestrictedManualCreate,
      prefillLikeCreate,
      selectedInternals,
      selectedExternals,
      internalDrafts,
      externalDrafts,
      existingInternalKeys,
      existingExternalIds,
    });
    if (!newInternals.length && !newExternals.length) {
      setError('Selecione ao menos uma subtarefa. Use Adicionar interna ou Adicionar chamado.');
      return;
    }
    const validation = await validateSubtaskPackage({
      selectedInternals: newInternals,
      selectedExternals: newExternals,
      internalDrafts,
      externalDrafts,
      workflows: incidentTargets.workflows,
    });
    if ('error' in validation) {
      setError(validation.error);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      let workItems = workGroups;
      let internalsForPayload = newInternals;
      if (catalogTemplatePersistMode) {
        const persisted = await persistInternalTemplateDraftsBeforePackage({
          slots: newInternals,
          internalDrafts,
          templatePersistMode: catalogTemplatePersistMode,
          currentUserId: user?.id,
          isChangeManager: manager,
          allowRequesterPackageEdit: reviewPermissions.canEditInternal,
        });
        if (persisted.length) {
          const areas = await changesApi.listWorkGroups({ only_active: true });
          const catalog = await changesApi
            .listInternalSubtaskTemplates({ only_active: true, catalog_only: manager })
            .catch(() => ({ items: [] }));
          workItems = augmentWorkGroupsWithCatalogTemplates(
            areas.items || [],
            catalog.items || [],
          );
          setWorkGroups(workItems);
          for (const row of persisted) {
            attachTemplateSlotToPackage(row.templateId, row.groupId, workItems);
          }
          internalsForPayload = newInternals.map((slot) => {
            if (slot.internal_subtask_template_id) return slot;
            const saved = persisted.find((row) => row.groupId === slot.group_id);
            if (!saved) return slot;
            return (
              flattenInternalSlots(workItems).find(
                (item) => slotReviewKey(item) === `${saved.groupId}:${saved.templateId}`,
              ) || slot
            );
          });
        }
      }
      const body = buildSubtaskPackagePayload({
        selectedInternals: internalsForPayload,
        selectedExternals: newExternals,
        internalDrafts,
        preparedExternals: validation.preparedExternals,
        workflows: incidentTargets.workflows,
      });
      await changesApi.createSubtaskPackage(change.id, body);
      const refreshed = await changesApi.getById(change.id);
      await uploadSubtaskPackageFiles(
        change.id,
        refreshed.subtasks || [],
        validation.preparedExternals,
      );
      await onSaved();
      onHide();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Não foi possível adicionar as subtarefas.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" backdrop="static" keyboard={false} scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          {prefillLikeCreate ? 'Redisparar internas e externas' : 'Adicionar subtarefas'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error ? (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}
        {!ready ? (
          <Alert variant="light">Carregando áreas e tipos de tarefa…</Alert>
        ) : (
          <>
            <Alert variant="secondary" className="small">
              Ao abrir, entram modelos com <strong>Mostrar na modal de disparo</strong> e{' '}
              <strong>grupo vinculado</strong> no cadastro (mesmo sem campos — monte na edição). Em{' '}
              <strong>Adicionar interna</strong>, todas as áreas ativas aparecem no combo. Modelos
              sem grupo vinculado só existem no cadastro até você associar um grupo. Linhas{' '}
              <strong>Aguardando disparo</strong> são gravadas ao clicar em{' '}
              <strong>Adicionar subtarefas</strong>; <strong>Já na mudança</strong> é só consulta.
            </Alert>
            <ChangeCreateSubtaskReview
              instancePackageMode
              internals={internalGroups}
              externals={externalGroups}
              leftoverInternals={leftoverInternals}
              leftoverExternals={leftoverExternals}
              internalDrafts={internalDrafts}
              externalDrafts={externalDrafts}
              workflows={incidentTargets.workflows}
              incidentTargetGroups={incidentTargets.groups}
              taskTypes={taskTypes}
              users={users}
              onToggleInternal={(groupId, selected) => patchInternal(groupId, { selected })}
              onPatchInternal={patchInternal}
              onToggleExternal={(lineKey, selected) => patchExternal(lineKey, { selected })}
              onPatchExternal={patchExternal}
              onAddInternal={(group) => {
                const manualSlot = unrestrictedManualCreate
                  ? {
                      ...group,
                      review_key: internalReviewKey(group.group_id, 0),
                      internal_subtask_template_id: undefined,
                      internal_subtask_template_name: undefined,
                      internal_templates: undefined,
                    }
                  : group;
                const baseKey = internalReviewKey(manualSlot.group_id, 0);
                let createdKey = baseKey;
                setReviewInternalKeys((prev) => {
                  if (!unrestrictedManualCreate && prev.includes(baseKey)) {
                    createdKey = baseKey;
                    return prev;
                  }
                  createdKey = unrestrictedManualCreate
                    ? nextDuplicateReviewKey(prev, baseKey)
                    : baseKey;
                  return [...prev, createdKey];
                });
                const base = emptyInternalDraft({ ...manualSlot, review_key: createdKey });
                setInternalDrafts((prev) => ({
                  ...prev,
                  [createdKey]: prev[createdKey] || base,
                }));
                if (!unrestrictedManualCreate) {
                  void enrichInternalDraftFromTemplate(manualSlot, base).then((enriched) => {
                    patchInternal(createdKey, enriched);
                  });
                }
                return createdKey;
              }}
              onAddExternal={(group) => {
                let createdKey = String(group.group_id);
                setReviewExternalKeys((prev) => {
                  createdKey = nextDuplicateReviewKey(prev, String(group.group_id));
                  return [...prev, createdKey];
                });
                setExternalDrafts((prev) => ({
                  ...prev,
                  [createdKey]: prev[createdKey] || emptyExternalDraft(group, incidentTargets.workflows),
                }));
                return createdKey;
              }}
              onConvertInternalToExternal={(group) => {
                const internalKey = group.review_key || slotReviewKey(group);
                setReviewInternalKeys((prev) => prev.filter((key) => key !== internalKey));
                const key = nextDuplicateReviewKey(
                  reviewExternalKeys,
                  String(group.group_id),
                );
                setReviewExternalKeys((prev) => [...prev, key]);
                setExternalDrafts((prev) => ({
                  ...prev,
                  [key]: prev[key] || emptyExternalDraft(group, incidentTargets.workflows),
                }));
              }}
              onRemoveInternal={(reviewKey) => {
                setReviewInternalKeys((prev) => prev.filter((key) => key !== reviewKey));
                setInternalDrafts((prev) => {
                  if (!prev[reviewKey]) return prev;
                  const next = { ...prev };
                  delete next[reviewKey];
                  return next;
                });
              }}
              onRemoveExternal={(lineKey) => {
                setReviewExternalKeys((prev) => prev.filter((key) => key !== lineKey));
                setExternalDrafts((prev) => {
                  if (!prev[lineKey]) return prev;
                  const next = { ...prev };
                  delete next[lineKey];
                  return next;
                });
              }}
              onPanelChange={setReviewEditing}
              allowInternalSubtasks={unrestrictedManualCreate || allowInternalSubtasks}
              allowManualInternalPackage
              allowRepeatSubtaskLines={unrestrictedManualCreate}
              reviewPermissions={reviewPermissions}
              templatePersistMode={catalogTemplatePersistMode}
              onTemplateSaved={handleTemplateSaved}
              onTemplateDeleted={handleTemplateDeleted}
            />
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {reviewEditing ? (
          <span className="me-auto small text-muted">Volte à lista para confirmar.</span>
        ) : null}
        <Button variant="outline-secondary" onClick={onHide} disabled={saving}>
          Cancelar
        </Button>
        <Button onClick={() => void handleSave()} disabled={saving || !ready || reviewEditing}>
          {saving ? 'Salvando…' : prefillLikeCreate ? 'Redisparar subtarefas' : 'Adicionar subtarefas'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ChangeAddSubtasksPackageModal;
